import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import { useEffect, useRef, useState } from 'react'
import { useRequest, useView } from '@10000/react'
import { AudioOff, AudioOn, Body, Box, Button, CenteredBox, H3, Play } from '../../widgets'
import { assetUrl } from '../../../utilities.js'
import { useBackground, useShell } from '../../layout'
import { SquizButton, usePracticeWheel } from '../common'

export const Tutorial = ({ active, displayMode }) => {
  const { presenter, stage, tutorialPlaying, options: { consensus, solo, free } = {} } = useView('squiz')
  const { hide } = usePracticeWheel()
  const request = useRequest('squiz')
  const videoRef = useRef()
  const [muted, setMuted] = useState(!displayMode && !solo)

  const [finishedTutorialVideo, setFinishedTutorialVideo] = useState(false)

  useShell({ active, mode: 'none' })
  useBackground().set({ active, background: null })

  useEffect(() => {
    if(videoRef.current && tutorialPlaying){
      setFinishedTutorialVideo(false)
      videoRef.current.play()
    }
  }, [videoRef, tutorialPlaying])

  useGSAP(() => {
    gsap.timeline()
      .call(hide)
      .to('h4', { opacity: 1, duration: 0.5 })
  })

  const startTutorial = () => request('tutorialStarted')
  const startSquiz = () => request('tutorialComplete')
  const toggleAudio = () => setMuted(!muted)

  const handleTutorialVideoEnded = () => {
    setFinishedTutorialVideo(true)
    if(presenter && !displayMode){
      return request('tutorialReset')
    }
  }

  const tutorialVideo = 'videos/tutorial-' +
    (stage === 'tutorial' ? 'standard' : '') +
    (stage === 'consensus-tutorial' ? 'consensus' : '') +
    (stage === 'results-tutorial' ? 'results' + (consensus ? '-consensus' : '-standard') : '') +
    (solo ? '-notimer' : '') +
    '.mp4'

  const buttonText = (stage === 'tutorial' ? 'Squiz' : '') +
    (stage === 'consensus-tutorial' ? 'Consensus' : '') +
    (stage === 'results-tutorial' ? 'Results' : '')

  useEffect(() => {
    const video = videoRef.current
    const onLoadedMetadata = () => {
      video.currentTime = 0.1
    }
    video.addEventListener("loadedmetadata", onLoadedMetadata)
    return () => video.removeEventListener("loadedmetadata", onLoadedMetadata)
  }, [])

  return (
    <CenteredBox fullWidth height="100%">
      {(presenter && !displayMode && !tutorialPlaying) && <>
        {finishedTutorialVideo
          ? <Box />
          : <Box position="absolute" top={56}>
              <H3>Tutorial</H3>
            </Box>
        }

        <Box position="absolute" bottom={32} gap={8} zIndex={11}>
          <SquizButton
            onClick={finishedTutorialVideo ? startSquiz : startTutorial}
            text={finishedTutorialVideo ? `Enter ${buttonText}` : `Play tutorial`}
            hideTools
          />
          {(free && !finishedTutorialVideo) && <Button light onClick={startSquiz}>Skip</Button>}
        </Box>
      </>}

      {(presenter && !displayMode && !free) &&
        <Box
          position="fixed"
          top={0}
          right={0}
          p={16}
          onClick={startSquiz}
          zIndex={11}
          cursor="pointer"
        >
          <Body alt bold underline italic>SKIP</Body>
        </Box>
      }

      <Box
        className={!tutorialPlaying ? "desktop-only" : ''}
        position="absolute"
        bottom={20}
        right={20}
        onClick={toggleAudio}
        zIndex={2}
      >
        {muted ? <AudioOff /> : <AudioOn />}
      </Box>

      <video
        src={assetUrl(tutorialVideo)}
        style={{ height: '100%', objectFit: 'contain' }}
        preload="metadata"
        onEnded={handleTutorialVideoEnded}
        playsInline
        ref={videoRef}
        muted={muted}
      />

      {(presenter && !displayMode && !tutorialPlaying) &&
        <CenteredBox
          position="absolute"
          inset
          onClick={() => !finishedTutorialVideo && request('tutorialStarted')}
          zIndex={10}
          cursor={finishedTutorialVideo ? 'default' : 'pointer'}
        >
          {finishedTutorialVideo
            ? <Button small onClick={startTutorial}>Replay</Button>
            : <Play size={200} />
          }
        </CenteredBox>
      }
    </CenteredBox>
  )
}